import logo from '@assets/logos/mrwolf-black-completo-160@2x.png';
import smallLogo from '@assets/logos/mrwolf-black-simple-160@2x.png';

import AvatarDropdown from '@components/AvatarDropdown';
import {
  AreaIcon24,
  LeftPanelCloseIcon24,
  LeftPanelOpenIcon24,
  NotificationIcon24,
  ServicesIcon24,
} from '@components/ui/WolfIcons';
import SidebarButton from '@components/ui/WolfSidebarButton';
import WolfSearchModal from '@components/WolfSearchModal';
import { getCurrentHeaders, graphQlClient, updateHeaders } from '@config/graphqlClient';
import DiscrepanciesGraphQL from '@graphql/discrepancy.queries';
import OrganizationsGraphQL from '@graphql/organization.queries';
import ProcessesGraphQL from '@graphql/process.queries';
import ProcessExecutionGraphQL from '@graphql/processExecution.queries';
import UserGraphQL from '@graphql/user.queries';
import { useTranslation } from '@hooks/useTranslation';
import { SnackType } from '@models/common.model';
import { getActiveDiscrepanciesAndPendingCountRequest } from '@models/discrepancy.model';
import { createOrUpdateOrganizationRequest } from '@models/organization.model';
import { getMyPendingExecutionsCountRequest } from '@models/processExecution.model';
import { getProcessesRequest, ProcessStatus } from '@models/process.model';
import { getSelfWithOrganizationRequest, UserLocale, UserRoles } from '@models/user.model';
import {
  AccountTreeOutlined,
  AttachFileOutlined,
  CorporateFareOutlined,
  CreditCard,
  Feedback,
  GroupWorkOutlined,
  PeopleAltOutlined,
  PlayCircleOutline,
  PolicyOutlined,
  Search,
  Subscriptions,
} from '@mui/icons-material';
import { Badge, Box, IconButton, List, styled, Typography } from '@mui/material';
import { useAppSelector } from '@redux/hooks';
import { appendActionMessage } from '@redux/reducers/actionMessages.reducer';
import { clearImpersonate } from '@redux/reducers/impersonate.reducer';
import { setLocale } from '@redux/reducers/locale.reducer';
import { setDiscrepancyCount, setPendingExecutionsCount } from '@redux/reducers/badges.reducer';
import { setOrganizationData } from '@redux/reducers/organization.reducer';
import {
  setImpersonating,
  setOnboardingSpotlight,
  setUserDetails,
  setUserOrganizations,
  updateSidebarStatus,
} from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import { useEffect, useRef, useState } from 'react';
import { batch, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import CollapsibleProgress from './CollapsibleProgress';
import WolfButton from './ui/WolfButton';
import WolfTooltip from './ui/WolfTooltip';
import packageJson from '../../../package.json';


const APP_VERSION = packageJson.version;

const SidebarContainer = styled(Box)(({ theme }) => ({
  width: 240,
  minWidth: 240,
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  position: 'fixed',
  top: 0,
  left: 0,
  borderRight: `1px solid ${theme.palette.primaryIcy.main}`,
  backgroundColor: theme.palette.background.default,
  zIndex: 1000,
}));

const CollapsedSidebarContainer = styled(SidebarContainer)(({ theme }) => ({
  width: 70,
  minWidth: 70,
  overflowX: 'hidden',
}));

const ScrollableContent = styled(Box)(({ theme }) => ({
  flex: 1,
  overflowY: 'auto',
  display: 'flex',
  flexDirection: 'column',
  overflowX: 'hidden',
}));

const FixedLogo = styled(Box)(({ theme }) => ({
  flexShrink: 0,
}));

const BottomSection = styled(Box)(({ theme }) => ({
  flexShrink: 0,
  padding: theme.spacing(2),
}));

const Logo = styled('img')(({ theme }) => ({
  height: 40,
  margin: theme.spacing(2, 0),
}));

export interface SidebarProps {}

const Sidebar: React.FC<SidebarProps> = () => {
  const localeMenu = useTranslation('menu');
  const localeCommon = useTranslation('common');
  const localeActionMessages = useTranslation('actionMessages');
  const localeTutorial = useTranslation('tutorial');
  const history = useHistory();
  const dispatch = useDispatch();
  const location = useLocation();

  // Check if Stripe is enabled
  const isStripeEnabled = !!process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY;

  const {
    avatar,
    firstName,
    lastName,
    role,
    collapsedSidebar,
    email,
    organizations,
    isImpersonating: userIsImpersonating,
    preferences,
  } = useAppSelector((state: RootState) => state.user);

  const {
    discrepancyCount,
    discrepancyCountLastUpdate,
    pendingExecutionsCount,
    pendingExecutionsCountLastUpdate,
  } = useAppSelector((state: RootState) => state.badges);

  // Check if badges should be shown based on notification preferences
  // For paired categories, we check both (they share the same badge)
  const showExecutionsBadge =
    preferences?.notificationPreferences?.assignments?.badge !== false &&
    preferences?.notificationPreferences?.executionReminders?.badge !== false;
  const showDiscrepanciesBadge =
    preferences?.notificationPreferences?.discrepancies?.badge !== false &&
    preferences?.notificationPreferences?.wolfPendings?.badge !== false;
  const { isImpersonating, impersonateName, impersonateId, originalOrganizationId, originalOrganizationName } =
    useAppSelector((state: RootState) => state.impersonate);
  const {
    organizationId,
    name: organizationName,
    onboardingDetails,
  } = useAppSelector((state: RootState) => state.organization);

  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [showSearch, setShowSearch] = useState<boolean>(false);

  const handleProcessCreation = () => {
    if (!onboardingDetails?.processCreated) {
      history.push('/processes?openDrawer=true');
    } else {
      history.push('/processes');
    }
  };

  const handleUserCreation = () => {
    if (!onboardingDetails?.userCreated) {
      history.push('/users?openDrawer=true');
    } else {
      history.push('/users');
    }
  };

  const handleExecutionRequested = async () => {
    if (!onboardingDetails?.executionRequested) {
      try {
        const data: getProcessesRequest = await graphQlClient.request(ProcessesGraphQL.queries.getProcesses);
        const processes = data.getProcesses;
        // Filter for processes that are marked as DONE
        const doneProcesses = processes?.filter((p) => p.status === ProcessStatus.DONE) || [];
        if (doneProcesses.length > 0) {
          const sortedProcesses = doneProcesses.sort((a, b) => (a.order > b.order ? 1 : -1));
          history.push(`/processes/${sortedProcesses[0].processId}`);
          // Show standalone spotlight for request execution after navigation
          setTimeout(() => {
            dispatch(
              setOnboardingSpotlight({
                stepId: 'request-execution',
                targetSelector: '[data-tutorial-id="request-execution-button"]',
                title: localeTutorial['requestExecution']?.title || 'Request Execution',
                content:
                  localeTutorial['requestExecution']?.content ||
                  'Click here to request a new execution of this process and assign it to a team member.',
                position: 'bottom',
              }),
            );
          }, 1500);
        }
        // If no done processes, do nothing (user needs to complete a process first)
      } catch (error) {
        console.error('Error checking processes:', error);
      }
    } else {
      history.push('/executions');
    }
  };

  const handleTriggerAdded = async () => {
    if (!onboardingDetails?.triggerAdded) {
      try {
        const data: getProcessesRequest = await graphQlClient.request(ProcessesGraphQL.queries.getProcesses);
        const processes = data.getProcesses;
        if (processes && processes.length > 0) {
          const sortedProcesses = processes.sort((a, b) => (a.order > b.order ? 1 : -1));
          history.push(`/processes/${sortedProcesses[0].processId}`);
          // Show standalone spotlight for triggers after navigation
          setTimeout(() => {
            dispatch(
              setOnboardingSpotlight({
                stepId: 'triggers',
                targetSelector: '[data-tutorial-id="triggers-section"]',
                title: localeTutorial['triggers']?.title || 'Triggers',
                content:
                  localeTutorial['triggers']?.content ||
                  'Triggers define when a process execution should happen. Event triggers respond to specific events, while Calendar triggers schedule executions at set times.',
                position: 'right',
              }),
            );
          }, 1500);
        } else {
          dispatch(
            appendActionMessage({
              message: localeMenu['createProcessFirst'],
              type: SnackType.WARNING,
            }),
          );
        }
      } catch (error) {
        console.error('Error checking processes:', error);
      }
    } else {
      history.push('/processes');
    }
  };

  const menuItems: {
    text: string;
    icon: React.ReactNode;
    link: string;
    popup?: boolean;
    action?: () => void;
    tutorialId?: string;
  }[] =
    organizations && organizations.length > 0
      ? [
          {
            text: localeMenu['processes'],
            icon: <GroupWorkOutlined />,
            link: '/processes',
            tutorialId: 'processes-menu',
          },
          {
            text: localeMenu['executions'],
            icon: showExecutionsBadge ? (
              <Badge badgeContent={pendingExecutionsCount} color="info" max={99}>
                <PlayCircleOutline />
              </Badge>
            ) : (
              <PlayCircleOutline />
            ),
            link: `/executions`,
            tutorialId: 'executions-menu',
          },

          {
            text: localeMenu['processMap'],
            icon: <AccountTreeOutlined />,
            link: '/processMap',
            tutorialId: 'process-map-menu',
          },
          {
            text: localeMenu['discrepancies'],
            icon: showDiscrepanciesBadge ? (
              <Badge badgeContent={discrepancyCount} color="info" max={99}>
                <NotificationIcon24 />
              </Badge>
            ) : (
              <NotificationIcon24 />
            ),
            link: `/discrepancies`,
          },
          { text: localeMenu['areas'], icon: <AreaIcon24 />, link: '/areas', tutorialId: 'areas-menu' },
          { text: localeMenu['services'], icon: <ServicesIcon24 />, link: '/services', tutorialId: 'services-menu' },
          { text: localeMenu['policies'], icon: <PolicyOutlined />, link: '/policies' },
          { text: localeMenu['documents'], icon: <AttachFileOutlined />, link: '/documents' },
        ]
      : [];

  const onboardingTasks = [
    {
      order: 1,
      label: localeMenu['processCreated'],
      completed: onboardingDetails?.processCreated || false,
      onClick: handleProcessCreation,
    },
    {
      order: 2,
      label: localeMenu['userCreated'],
      completed: onboardingDetails?.userCreated || false,
      onClick: handleUserCreation,
    },
    {
      order: 3,
      label: localeMenu['executionRequested'],
      completed: onboardingDetails?.executionRequested || false,
      onClick: handleExecutionRequested,
    },
    {
      order: 4,
      label: localeMenu['triggerAdded'],
      completed: onboardingDetails?.triggerAdded || false,
      onClick: handleTriggerAdded,
    },
  ];
  if ([UserRoles.SUPER_ADMIN, UserRoles.OWNER, UserRoles.ADMIN].includes(role as UserRoles)) {
    menuItems.push({
      text: localeMenu['users'],
      icon: <PeopleAltOutlined />,
      link: `/users`,
    });
  }

  if ([UserRoles.SUPER_ADMIN].includes(role as UserRoles)) {
    menuItems.unshift({
      text: localeMenu['organizations'],
      icon: <CorporateFareOutlined />,
      link: `/organizations`,
    });
    if (isStripeEnabled) {
      menuItems.push({
        text: localeMenu['subscriptions'],
        icon: <Subscriptions />,
        link: `/subscriptions`,
      });
    }
    menuItems.push({
      text: localeMenu['viewFeedback'],
      icon: <Feedback />,
      link: `/feedback`,
    });
  }

  if ([UserRoles.OWNER, UserRoles.SUPER_ADMIN].includes(role as UserRoles)) {
    if (isStripeEnabled) {
      menuItems.push({
        text: localeMenu['billing'],
        icon: <CreditCard />,
        link: `/billing`,
      });
    }
  }

  useEffect(() => {
    const currentPath = location.pathname || '';
    const cleanedPath = currentPath.replace(/\/$/, ''); // remove ending slash

    const matchedItem = menuItems.find((item) => cleanedPath.startsWith(item.link));
    if (matchedItem) {
      setSelectedItem(matchedItem.text);
    } else {
      setSelectedItem(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  useEffect(() => {
    try {
      const headers = getCurrentHeaders();

      // If user was impersonating but headers don't exist, restore them
      if (userIsImpersonating && impersonateId && !headers.Impersonate) {
        updateHeaders({ Impersonate: `Target ${impersonateId}` });
      }
      // If user is not impersonating but headers exist, clear stale headers
      else if ((!userIsImpersonating && headers.Impersonate) || (userIsImpersonating && !impersonateId)) {
        console.warn('Detected stale impersonation headers, clearing them');

        // Clear the stale impersonation header
        updateHeaders({});

        // Clear impersonation flags
        dispatch(setImpersonating(false));
        dispatch(clearImpersonate());

        // Reload user info to get back to original user state
        const reloadUserInfo = async () => {
          try {
            const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
              UserGraphQL.queries.getSelfWithOrganization,
            );
            const user = userData.getSelfWithOrganization.user;
            const role = userData.getSelfWithOrganization.role;
            const organization = userData.getSelfWithOrganization.organization;
            const organizations = userData.getSelfWithOrganization.organizations || [];

            // Batch all Redux updates to trigger only one re-render
            batch(() => {
              if (organization) {
                dispatch(setOrganizationData(organization));
              }
              dispatch(setUserDetails({ ...user, role }));
              dispatch(setUserOrganizations(organizations));
              dispatch(setLocale(user.locale || UserLocale.EN));

              // Show user notification that impersonation was cleared
              dispatch(
                appendActionMessage({
                  message: localeActionMessages['staleImpersonationCleared'],
                  type: SnackType.WARNING,
                }),
              );
            });
          } catch (error) {
            console.error('Error reloading user info after clearing impersonation:', error);
            dispatch(
              appendActionMessage({
                message: localeActionMessages['requestError'],
                type: SnackType.ERROR,
              }),
            );
          }
        };

        reloadUserInfo();
      }
    } catch (error) {
      console.error('Error handling impersonation state:', error);
      // Ensure headers are cleared on any error
      updateHeaders({});
      dispatch(
        appendActionMessage({
          message: localeActionMessages['requestError'],
          type: SnackType.ERROR,
        }),
      );
    }
  }, [userIsImpersonating, impersonateId, dispatch, localeActionMessages]);

  const fetchDiscrepancyCount = async () => {
    try {
      const data: getActiveDiscrepanciesAndPendingCountRequest = await graphQlClient.request(
        DiscrepanciesGraphQL.queries.getActiveDiscrepanciesAndPendingCount,
      );
      if (data?.getActiveDiscrepanciesAndPendingCount) {
        const totalCount =
          data.getActiveDiscrepanciesAndPendingCount.activeDiscrepanciesCount +
          data.getActiveDiscrepanciesAndPendingCount.activePendingCount;
        dispatch(setDiscrepancyCount(totalCount));
      }
    } catch (error) {
      console.error('Error fetching discrepancy count:', error);
    }
  };

  const fetchPendingExecutionsCount = async () => {
    try {
      const data: getMyPendingExecutionsCountRequest = await graphQlClient.request(
        ProcessExecutionGraphQL.queries.getMyPendingExecutionsCount,
      );
      if (data?.getMyPendingExecutionsCount !== undefined) {
        dispatch(setPendingExecutionsCount(data.getMyPendingExecutionsCount));
      }
    } catch (error) {
      console.error('Error fetching pending executions count:', error);
    }
  };

  // Track previous organizationId to detect actual org changes (initialized to undefined to skip first mount)
  const prevOrgIdRef = useRef<string | null | undefined>(undefined);
  // Track if mount effect has run to coordinate with org change effect
  const mountEffectRanRef = useRef(false);

  const shouldFetchDiscrepancies = () => {
    if (!showDiscrepanciesBadge) return false;
    if (!discrepancyCountLastUpdate) return true;

    const lastUpdate = new Date(discrepancyCountLastUpdate);
    const now = new Date();
    const minutesSinceLastUpdate = (now.getTime() - lastUpdate.getTime()) / (1000 * 60);

    return minutesSinceLastUpdate >= 1;
  };

  const shouldFetchPendingExecutions = () => {
    if (!showExecutionsBadge) return false;
    if (!pendingExecutionsCountLastUpdate) return true;

    const lastUpdate = new Date(pendingExecutionsCountLastUpdate);
    const now = new Date();
    const minutesSinceLastUpdate = (now.getTime() - lastUpdate.getTime()) / (1000 * 60);

    return minutesSinceLastUpdate >= 1;
  };

  // Effect for handling organization changes - fetch immediately on org switch
  useEffect(() => {
    // Skip if mount effect hasn't run yet (avoid race condition)
    if (!mountEffectRanRef.current) {
      prevOrgIdRef.current = organizationId;
      return;
    }

    const orgChanged = prevOrgIdRef.current !== organizationId && prevOrgIdRef.current !== undefined;
    prevOrgIdRef.current = organizationId;

    if (orgChanged && organizationId) {
      // Organization changed - fetch counts for new org immediately
      if (showDiscrepanciesBadge) fetchDiscrepancyCount();
      if (showExecutionsBadge) fetchPendingExecutionsCount();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId, showDiscrepanciesBadge, showExecutionsBadge]);

  // Effect for initial fetch and periodic refresh
  useEffect(() => {
    // Mark that mount effect has run (coordinates with org change effect)
    mountEffectRanRef.current = true;
    prevOrgIdRef.current = organizationId;

    // Initial fetch if needed (timestamps are null or 10+ minutes old)
    if (shouldFetchDiscrepancies()) {
      fetchDiscrepancyCount();
    }
    if (shouldFetchPendingExecutions()) {
      fetchPendingExecutionsCount();
    }

    let interval: NodeJS.Timeout | null = null;

    const fetchAllCounts = () => {
      if (showDiscrepanciesBadge) fetchDiscrepancyCount();
      if (showExecutionsBadge) fetchPendingExecutionsCount();
    };

    const startInterval = () => {
      // Set up periodic check every 10 minutes
      interval = setInterval(fetchAllCounts, 60 * 1000);
    };

    const stopInterval = () => {
      if (interval) {
        clearInterval(interval);
        interval = null;
      }
    };

    const handleVisibilityChange = () => {
      if (document.hidden) {
        stopInterval();
      } else {
        // Only fetch if enough time has passed
        if (shouldFetchDiscrepancies()) {
          fetchDiscrepancyCount();
        }
        if (shouldFetchPendingExecutions()) {
          fetchPendingExecutionsCount();
        }
        startInterval();
      }
    };

    // Start interval if page is visible
    if (!document.hidden) {
      startInterval();
    }

    // Add visibility change listener
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup
    return () => {
      stopInterval();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
    // Only run on mount - org changes handled by separate effect
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleMenuItemClick = (
    link: string,
    text: string,
    popup?: boolean,
    action?: () => void,
    event?: React.MouseEvent,
  ) => {
    if (action) {
      action();
    } else if (popup) {
      //setShowPopup(true);
    } else {
      // Check if ctrl/cmd key is pressed for opening in new tab
      if (event && (event.ctrlKey || event.metaKey)) {
        window.open(link, '_blank');
      } else {
        setSelectedItem(text);
        history.push(link);
      }
    }
  };

  const toggleCollapse = () => {
    if (isImpersonating) {
      dispatch(
        appendActionMessage({
          message: localeMenu['stopImpersonating'],
          type: SnackType.ERROR,
        }),
      );
    } else {
      dispatch(updateSidebarStatus());
    }
  };

  const openSearch = () => {
    setShowSearch(true);
  };

  const handleStopImpersonate = async () => {
    try {
      // Step 1: Clear impersonation header first
      updateHeaders({});

      // Step 2: Set organization context to original org BEFORE making GraphQL call
      // This ensures the backend returns the correct role for the original organization
      dispatch(
        setOrganizationData({
          organizationId: originalOrganizationId,
          name: originalOrganizationName,
          description: '',
        }),
      );

      // Step 3: Temporarily set X-Organization-Id header to ensure correct context
      const currentHeaders = getCurrentHeaders();
      updateHeaders({
        ...currentHeaders,
        'X-Organization-Id': originalOrganizationId,
      });

      // Step 4: Fetch the original user's data with proper organization context
      const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
        UserGraphQL.queries.getSelfWithOrganization,
      );

      const user = userData.getSelfWithOrganization.user;
      const role = userData.getSelfWithOrganization.role;
      const organization = userData.getSelfWithOrganization.organization;
      const organizations = userData.getSelfWithOrganization.organizations;

      // Step 5: Batch all Redux updates to trigger only one re-render
      batch(() => {
        dispatch(
          setOrganizationData({
            organizationId: organization.organizationId,
            name: organization.name,
            description: organization.description || '',
            onboardingDetails: organization.onboardingDetails,
            lastProfileUpdate: organization.lastProfileUpdate,
          }),
        );
        dispatch(setUserDetails({ ...user, role }));
        dispatch(setUserOrganizations(organizations || []));
        dispatch(setLocale(user.locale || UserLocale.EN));
        dispatch(clearImpersonate());
        dispatch(setImpersonating(false));
      });

      // Step 6: Clear the temporary X-Organization-Id header
      updateHeaders({});

      // Step 7: Navigate to processes page only if not already there
      if (location.pathname !== '/processes') {
        history.push('/processes');
      }

      dispatch(
        appendActionMessage({
          message: localeActionMessages['impersonateCleared'] || 'Stopped impersonating',
          type: SnackType.SUCCESS,
        }),
      );
    } catch (e) {
      console.error('Error stopping impersonation:', e);
      dispatch(appendActionMessage({ message: localeActionMessages['impersonateClearFailed'], type: SnackType.ERROR }));
    }
  };

  const updateOnboardingDismiss = async (type: 'primary' | 'secondary') => {
    try {
      let onboardingDetails = null;
      if (type === 'primary') {
        onboardingDetails = {
          processCreated: true,
          userCreated: true,
          executionRequested: true,
          triggerAdded: true,
          ftueCompleted: true,
        };
      } else if (type === 'secondary') {
        const twoWeeksLater = new Date();
        twoWeeksLater.setDate(twoWeeksLater.getDate() + 14);
        onboardingDetails = { skippedUntil: twoWeeksLater.toISOString() };
      }
      const data: createOrUpdateOrganizationRequest = await graphQlClient.request(
        OrganizationsGraphQL.mutations.updateOrganization,
        {
          organization: {
            organizationId: organizationId || '',
            onboardingDetails,
          },
        },
      );
      if (data?.updateOrganization?.organizationId) {
        dispatch(setOrganizationData(data.updateOrganization));
      }
    } catch (error) {
      console.error('Failed to update onboarding dismiss status:', error);
    }
  };

  const skippedOld = onboardingDetails?.skippedUntil && new Date(onboardingDetails.skippedUntil) < new Date();
  const onboardingCount = onboardingTasks.filter((task) => task.completed).length;
  const totalOnboardingTasks = onboardingTasks.length;

  return (
    <>
      {collapsedSidebar && !isImpersonating ? (
        <CollapsedSidebarContainer data-tutorial-id="sidebar">
          <FixedLogo>
            <Box display="flex" alignItems="center" justifyContent="center">
              <Logo src={smallLogo} alt="Mr. Wolf" />
            </Box>
          </FixedLogo>
          <ScrollableContent id="sidebar-scrollable-content">
            {organizations && organizations.length > 0 && (
              <Box className="lg:my-6 p-8 flex items-center !h-14 justify-center cursor-pointer" onClick={openSearch}>
                <WolfTooltip title={localeMenu['searchTooltip']}>
                  <span>
                    <Search />
                  </span>
                </WolfTooltip>
              </Box>
            )}
            {organizations && organizations.length > 0 && onboardingCount < totalOnboardingTasks && (
              <Box className="mx-6 my-3">
                <WolfTooltip title={localeMenu['onboardingTasksCompleted']}>
                  <Typography variant="body14semibold">{onboardingCount + '/' + totalOnboardingTasks}</Typography>
                </WolfTooltip>
              </Box>
            )}
            <List>
              {menuItems.map((item) => (
                <Box key={item.text} sx={{ width: '100%' }}>
                  <SidebarButton
                    icon={item.icon}
                    label={''}
                    tooltip={item.text}
                    selected={selectedItem === item.text}
                    onClick={(e) => {
                      handleMenuItemClick(item.link, item.text, item.popup, item.action, e);
                    }}
                    collapsed
                    premium={item.popup}
                    {...(item.tutorialId && { 'data-tutorial-id': item.tutorialId })}
                  />
                </Box>
              ))}
            </List>
          </ScrollableContent>
          <BottomSection>
            <Box className="flex flex-wrap items-center justify-center">
              <AvatarDropdown avatar={avatar || ''} />
              <WolfTooltip title={localeMenu['expandTooltip']}>
                <IconButton onClick={toggleCollapse}>
                  <LeftPanelOpenIcon24 />
                </IconButton>
              </WolfTooltip>
            </Box>
          </BottomSection>
        </CollapsedSidebarContainer>
      ) : (
        <SidebarContainer data-tutorial-id="sidebar">
          <FixedLogo>
            <Box className="flex flex-wrap items-center justify-center">
              <Logo src={logo} alt="Mr. Wolf" />
              <span className="block text-small w-full text-center">{APP_VERSION}</span>
            </Box>
          </FixedLogo>
          <ScrollableContent id="sidebar-scrollable-content">
            {organizations && organizations.length > 0 && (
              <Box className="lg:my-6 p-6 flex items-center !h-14 cursor-pointer" onClick={openSearch}>
                <WolfTooltip title={localeMenu['searchTooltip']}>
                  <span>
                    <Search />
                  </span>
                </WolfTooltip>
                <Typography className="!ml-2" variant="body14">
                  {localeCommon['search']}
                </Typography>
              </Box>
            )}
            {organizations && organizations.length > 0 && (!onboardingDetails?.skippedUntil || skippedOld) && (
              <CollapsibleProgress
                tasks={onboardingTasks}
                title={localeMenu['onboarding']}
                dismissTitle={localeMenu['dismissPermanently']}
                dismissTitleSecondary={localeMenu['dismissForTwoWeeks']}
                modalTitle={localeMenu['dismissOnboarding']}
                modalText={localeMenu['dismissOnboardingText']}
                handleDismiss={updateOnboardingDismiss}
              />
            )}
            <List className="flex flex-col items-start w-full">
              {menuItems.map((item) => (
                <Box key={item.text} sx={{ width: '100%' }}>
                  <SidebarButton
                    icon={item.icon}
                    label={item.text}
                    selected={selectedItem === item.text}
                    onClick={(e) => {
                      handleMenuItemClick(item.link, item.text, item.popup, item.action, e);
                    }}
                    premium={item.popup}
                    {...(item.tutorialId && { 'data-tutorial-id': item.tutorialId })}
                  />
                </Box>
              ))}
            </List>
          </ScrollableContent>
          <BottomSection>
            {isImpersonating && (
              <Box className="flex flex-wrap text-center justify-center mb-4">
                <Typography color="error" variant="h4semibold">
                  {localeMenu['workingAs']} {impersonateName}
                  <br />
                  <small className="text-xs">{localeMenu['ifReload']}</small>
                </Typography>
                <WolfButton variant="contained" color="secondary" onClick={handleStopImpersonate}>
                  {localeMenu['stopImpersonate']}
                </WolfButton>
              </Box>
            )}
            <Box className="flex items-center justify-center mb-1 px-2">
              <WolfTooltip title={organizationName || 'Loading...'} placement='right'>
                <Typography
                  variant="body12"
                  color="primary"
                  textAlign="center"
                  component="div"
                  sx={{ fontWeight: 600 }}>
                  {organizationName
                    ? organizationName.length > 25
                      ? organizationName.slice(0, 25) + '...'
                      : organizationName
                    : 'Loading...'}
                </Typography>
              </WolfTooltip>
            </Box>
            <Box className="flex items-center justify-between">
              <AvatarDropdown avatar={avatar || ''} firstName={firstName || ''} lastName={lastName || ''} email={email || ''} />
              <WolfTooltip title={localeMenu['collapseTooltip']}>
                <IconButton onClick={toggleCollapse}>
                  <LeftPanelCloseIcon24 />
                </IconButton>
              </WolfTooltip>
            </Box>
          </BottomSection>
        </SidebarContainer>
      )}

      {<WolfSearchModal open={showSearch} onClose={() => setShowSearch(false)} />}

    </>
  );
};

export default Sidebar;
